import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80";

const HeroSection = ({ content, images }) => {
  const heroImage = images && images.length > 0 
    ? `${BACKEND_URL}${images[0].url}` 
    : DEFAULT_HERO_IMAGE;

  const extra = content?.extra_fields || {};

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="min-h-screen pt-20 flex items-center bg-gradient-to-br from-white via-slate-50 to-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1e3a8a]" style={{ clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1 text-center lg:text-left">
            <div className="mb-6">
              <span className="inline-block px-4 py-1.5 bg-[#1e3a8a]/5 text-[#1e3a8a] text-sm font-inter font-medium tracking-wide uppercase rounded-full">
                {extra.badge_text || 'Association régionale'}
              </span>
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight mb-6">
              <span className="block">Place Financière</span>
              <span className="block text-[#1e3a8a]">Bourgogne-Franche-Comté</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 font-inter leading-relaxed mb-4">
              {content?.subtitle || 'Les professionnels de la finance et du tertiaire au service des entreprises'}
            </p>
            
            <p className="text-base text-slate-500 font-inter leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              {content?.content || 'Fédérer les acteurs économiques, financiers, juridiques et institutionnels au service du développement des entreprises.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={(e) => scrollToSection(e, '#contact')}
                data-testid="hero-contact-btn"
                className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-8 py-6 text-base font-inter font-medium rounded-full transition-all duration-300 hover:shadow-xl btn-ripple"
              >
                {extra.cta_primary || 'Nous contacter'}
              </Button>
              <Button
                onClick={(e) => scrollToSection(e, '#missions')}
                data-testid="hero-discover-btn"
                variant="outline"
                className="border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white px-8 py-6 text-base font-inter font-medium rounded-full transition-all duration-300"
              >
                {extra.cta_secondary || 'Découvrir notre mission'}
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-lg">
              {/* Gold frame offset */}
              <div className="absolute top-6 left-6 right-0 bottom-0 border-4 border-[#D4AF37] -z-10" />
              
              <img
                src={heroImage}
                alt="Architecture financière moderne"
                className="w-full h-[400px] lg:h-[500px] object-cover shadow-2xl"
                data-testid="hero-image"
              />
              
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-xl">
                <p className="text-3xl font-playfair font-bold text-[#1e3a8a]">{extra.year_badge || '2017'}</p>
                <p className="text-sm font-inter text-slate-500">{extra.year_label || 'Depuis'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={(e) => scrollToSection(e, '#about')}
          className="text-slate-400 hover:text-[#1e3a8a] transition-colors"
          aria-label="Défiler vers le bas"
          data-testid="scroll-indicator"
        >
          <ChevronDown size={32} />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
